import React, { createContext, useEffect, useReducer } from "react";
import { Backend } from "../helpers";
import { LoadingView } from "../views";
import { notification } from "antd";
import { AxiosError } from "axios";

export interface IAuthData {
  token: string;
}

export interface IAppData {
  initializing: boolean;
  loading: boolean;
  data?: IAuthData;
}

export interface IAppContextDispatchAction {
  type: "updateState" | "startLoading" | "stopLoading" | "logout";
  state?: Partial<IAppData> | undefined;
}

export interface IAuthContext {
  auth: IAppData;
  updateAuth: (values: Partial<IAppData>) => void;
  dispatch: React.Dispatch<IAppContextDispatchAction>;
}

export const authContext = createContext<IAuthContext>({
  auth: {
    initializing: true,
    loading: false,
    data: undefined,
  },
  updateAuth: () => {},
  dispatch: () => {},
});

const AuthProvider: React.FC = (props) => {
  /**
   * States management
   */
  const reducer = (
    state: IAppData,
    action: IAppContextDispatchAction
  ): IAppData => {
    switch (action.type) {
      case "updateState":
        return {
          initializing: action.state?.initializing ?? state.initializing,
          loading: action.state?.loading ?? state.loading,
          data: action.state?.data ?? state.data,
        };

      case "startLoading":
        return {
          initializing: state.initializing,
          loading: true,
          data: state.data,
        };

      case "stopLoading":
        return {
          initializing: state.initializing,
          loading: false,
          data: state.data,
        };

      case "logout":
        // remove local data
        window.localStorage.removeItem("authData");
        // clear backend token
        Backend.updateToken();
        return {
          initializing: state.initializing,
          loading: false,
          data: undefined,
        };

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    initializing: true,
    loading: false,
    data: undefined,
  });

  useEffect(() => {
    try {
      dispatch({
        type: "updateState",
        state: {
          initializing: true,
        },
      });
      const saved = window.localStorage.getItem("authData");

      if (saved) {
        const authData: IAuthData = JSON.parse(saved);

        // update backend token
        Backend.updateToken(authData.token);

        dispatch({
          type: "updateState",
          state: {
            data: authData,
          },
        });
      }

      /**
       * Axios interceptor
       */
      Backend.getInstance()
        .axios()
        .interceptors.response.use(
          function (response) {
            return response;
          },
          function (error: AxiosError<any>) {
            if (error?.response?.status === 401) {
              // notify the use
              notification.warn({
                message: "Unauthorized access",
                description:
                  "Your session has probably expired. Please login again.",
              });
              // logout
              dispatch({ type: "logout" });
            } else {
              return Promise.reject(error);
            }
          }
        );

      console.log("App state initiated");
    } catch (error) {
      console.log(
        "Failed to load auth data",
        window.localStorage.getItem("authData")
      );
    } finally {
      dispatch({
        type: "updateState",
        state: {
          initializing: false,
        },
      });
    }
  }, []);

  const updateAuth = (values: Partial<IAppData>) => {
    if (values.data) {
      // store the access token localy
      window.localStorage.setItem("authData", JSON.stringify(values.data));

      // update backend token
      Backend.updateToken(values.data.token);
    }

    /**
     * Always call it at last
     */
    dispatch({
      type: "updateState",
      state: {
        loading: values.loading,
        data: values.data,
      },
    });
  };

  return (
    <authContext.Provider value={{ auth: state, updateAuth, dispatch }}>
      {state.initializing ? <LoadingView /> : props.children}
    </authContext.Provider>
  );
};

export default AuthProvider;
